import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import moment from 'moment';
import { waitForFirebaseAuth } from './firebaseUtils';
import { backendUrl } from './backendUrl';

/**
 * Global Secure API Service based on native Fetch (Migrated from user project)
 */
export const apiService = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : backendUrl(endpoint);
  
  // 1. Ensure we have the latest token
  let { currentUser } = firebase.auth();
  if (!currentUser) {
    currentUser = await waitForFirebaseAuth();
  }
  
  const token = currentUser ? await currentUser.getIdToken().catch(() => null) : null;
  
  // 2. Prepare Headers
  const headers = { 
    ...options.headers,
    'Timezone': moment().utcOffset().toString(),
    'Appversion': import.meta.env.VITE_APP_VERSION || '31601'
  };
  
  // Only set application/json if not FormData
  const isFormData = options.body instanceof FormData;
  
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  return fetch(url, config).then(async (response) => {
    // Standard response formatting to match Axios behavior (response.data)
    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else if (options.responseType === 'blob') {
      data = await response.blob();
    } else {
      data = await response.text();
    }

    // Axios-like response object
    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config,
    };
  });
};

export const api = {
  post: (endpoint, body, options = {}) => apiService(endpoint, { 
    ...options, 
    method: 'POST', 
    body: (body instanceof FormData || typeof body === 'string') ? body : JSON.stringify(body) 
  }),
  get: (endpoint, options = {}) => {
    let url = endpoint;
    if (options.params) {
      const params = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value);
        }
      });
      const queryString = params.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }
    return apiService(url, { ...options, method: 'GET', ...options });
  },
  put: (endpoint, body, options = {}) => apiService(endpoint, { 
    ...options, 
    method: 'PUT', 
    body: (body instanceof FormData || typeof body === 'string') ? body : JSON.stringify(body) 
  }),
  patch: (endpoint, body, options = {}) => apiService(endpoint, { 
    ...options, 
    method: 'PATCH', 
    body: (body instanceof FormData || typeof body === 'string') ? body : JSON.stringify(body) 
  }),
  delete: (endpoint, options = {}) => apiService(endpoint, { ...options, method: 'DELETE' }),
};
